import type { MenuConfig } from '@blocksuite/affine-components/context-menu';
import { createIdentifier } from '@blocksuite/global/di';
import type { ExtensionType } from '@blocksuite/store';

import { EditorHostKey } from '../shared/editor-host-context.js';

/** The table row a row-menu extension adds its items for. */
export interface RowMenuExtensionContext {
  readonly workspaceId: string;
  /** Doc that holds the database block. */
  readonly docId: string;
  /** Logical database id, as the row events and the server name it. */
  readonly databaseId: string;
  readonly rowId: string;
}

/**
 * Items a host (StreamLain) adds to the menu of one table row. Both table
 * views (`table/pc` and `table/pc-virtual`) show them.
 */
export interface RowMenuExtension {
  readonly id: string;
  items(context: RowMenuExtensionContext): MenuConfig[];
}

export const RowMenuExtensionIdentifier = createIdentifier<RowMenuExtension>(
  'data-view-row-menu-extension'
);

export function RowMenuExtensionRegistration(
  extension: RowMenuExtension
): ExtensionType {
  return {
    setup(di) {
      di.addImpl(RowMenuExtensionIdentifier(extension.id), () => extension);
    },
  };
}

type RowMenuDataSourceLike = {
  databaseId?: string;
  docId?: string;
  workspaceId?: string;
  serviceGet?: (key: unknown) => unknown;
};

type RowMenuHostLike = {
  std?: {
    provider?: {
      getAll?: (
        identifier: typeof RowMenuExtensionIdentifier
      ) => Map<string, RowMenuExtension>;
    };
  };
};

/**
 * The extension items for one row. A data view without a database block
 * (no logical database id) or without a host has no row to name, so it
 * gets no extension items.
 */
export function rowMenuExtensions(
  dataSource: unknown,
  rowId: string
): MenuConfig[] {
  const source = dataSource as RowMenuDataSourceLike;
  const { databaseId, docId, workspaceId } = source;
  if (!databaseId || !docId || !workspaceId) return [];
  const host = source.serviceGet?.(EditorHostKey) as
    | RowMenuHostLike
    | null
    | undefined;
  const extensions = host?.std?.provider?.getAll?.(RowMenuExtensionIdentifier);
  if (!extensions?.size) return [];
  const context: RowMenuExtensionContext = {
    workspaceId,
    docId,
    databaseId,
    rowId,
  };
  return [...extensions.values()].flatMap(extension =>
    extension.items(context)
  );
}
